import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { push, ref, update } from "firebase/database";
import { db } from "../firebase";
import { useFirebase } from "../FbContex";
import { INode } from "../@types/device";
import { NodeType, Widget } from "../enum";
import {
  allSources,
  allNodes,
  allRooms,
  isTelemetry,
  nextOrder,
  nodesOfRoom,
} from "../lib/selectors";
import { asArray, prune, rgbKeys, stripNulls, writeTargets } from "../lib/node";
import WidgetParams, { ParamState, paramCount } from "./form/WidgetParams";
import { TextField, NumberField, SelectField, SwitchField } from "./form/Fields";
import { DIALOG_BACKDROP, DIALOG_PANEL } from "./tableStyles";

type Param = {
  device: INode | null;
  close: () => void;
};

const EMPTY: ParamState = {
  buttons: [],
  inputs: [],
  reads: [],
  slider: {},
  gauge: {},
  rgb: { r: "", g: "", b: "" },
  ac: {},
};

export default function DeviceForm({ device, close }: Param) {
  // `state` is already a prop name of WidgetParams in this file.
  const { rooms, devices, sources, state: liveState } = useFirebase();

  const [title, setTitle] = useState("");
  const [stateKey, setStateKey] = useState("");
  const [roomId, setRoomId] = useState("");
  const [addr, setAddr] = useState("");
  const [icon, setIcon] = useState("");
  const [widget, setWidget] = useState<Widget | "">("");
  const [type, setType] = useState<NodeType | "">("");
  const [order, setOrder] = useState<number | undefined>();
  const [disabled, setDisabled] = useState(false);
  const [params, setParams] = useState<ParamState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!device) return;
    setTitle(device.title || "");
    setStateKey(device.stateKey || "");
    setRoomId(device.roomId || "");
    setAddr(device.addr || "");
    setIcon(device.icon || "");
    setWidget((device.widget as Widget) || "");
    setType((device.type as NodeType) || "");
    setOrder(
      device.order ??
        nextOrder(device.roomId ? nodesOfRoom(devices, device.roomId) : allNodes(devices))
    );
    setDisabled(device.disabled ?? false);
    setParams({
      buttons: asArray(device.buttons),
      inputs: asArray(device.inputs),
      reads: asArray(device.reads),
      slider: device.slider ?? {},
      gauge: device.gauge ?? {},
      rgb: device.rgb ?? { r: "", g: "", b: "" },
      ac: device.ac ?? {},
    });
    setError("");
    // Only reset when a different device is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const setParam = <K extends keyof ParamState>(k: K, v: ParamState[K]) =>
    setParams((p) => ({ ...p, [k]: v }));

  const roomOptions = useMemo(
    () => allRooms(rooms).map((r) => ({ value: r.id, label: r.name })),
    [rooms]
  );

  const sensorOnly = isTelemetry(sources, addr);

  /**
   * The synthetic "missing source" option matters: without it, editing a device
   * whose source was deleted silently blanks `addr` and the next save writes
   * garbage.
   */
  const sourceOptions = useMemo(() => {
    const opts = allSources(sources).map((e) => ({
      value: e.id,
      label: e.label || e.id,
    }));
    if (addr && !sources[addr])
      opts.push({ value: addr, label: `${addr} (missing source)` });
    return opts;
  }, [sources, addr]);

  const liveKeys = useMemo(
    () => Object.keys(liveState[addr] ?? {}).sort(),
    [liveState, addr]
  );

  /**
   * Disable rather than hide, and never auto-reset: switching Address would
   * silently wipe the Parameters tab the user just filled in, and the loss is
   * invisible because it is on the other tab. A disabled option still displays
   * when it is the current value, so the state stays reversible.
   */
  const widgetOptions = useMemo(
    () =>
      Object.values(Widget).map((w) => {
        const writes = w !== Widget.READOUT && w !== Widget.GAUGE;
        return {
          value: w,
          label: sensorOnly && writes ? `${w} -- writes` : w,
          disabled: sensorOnly && writes,
        };
      }),
    [sensorOnly]
  );

  const save = () => {
    if (!title.trim()) return setError("Title is required.");
    if (!stateKey.trim()) return setError("State key is required.");
    if (!roomId) return setError("Room is required.");
    if (!addr) return setError("Address is required.");
    if (!widget) return setError("Widget is required.");

    // The real guard. The disabled widget options above are UX; this catches
    // the cases a widget filter cannot: extra inputs under a read-only widget,
    // and a per-input addr override pointing back at a sensor tree.
    const bad = writeTargets({
      addr,
      stateKey: stateKey.trim(),
      widget,
      rgb: params.rgb,
      ac: params.ac,
      inputs: params.inputs,
    } as INode).find((t) => isTelemetry(sources, t.addr));
    if (bad)
      return setError(
        `${bad.addr}/${bad.key} is on a telemetry source. Telemetry is written by the boards, not by the app.`
      );

    setError("");
    setLoading(true);

    const rgb =
      widget === Widget.COLOR
        ? {
            r: params.rgb.r || `${stateKey}r`,
            g: params.rgb.g || `${stateKey}g`,
            b: params.rgb.b || `${stateKey}b`,
          }
        : null;

    // Every parameter key is named explicitly with null for the ones that no
    // longer apply. That deletes stale sub-objects when the widget changes --
    // the old set() silently dropped whatever the form did not know about.
    const payload: { [k: string]: any } = prune({
      title: title.trim(),
      stateKey: stateKey.trim(),
      roomId,
      addr,
      icon: icon.trim(),
      widget,
      type: type || null,
      order: order ?? nextOrder(nodesOfRoom(devices, roomId)),
      disabled,
      buttons: widget === Widget.BUTTONS && params.buttons.length ? params.buttons : null,
      slider: widget === Widget.SLIDER ? params.slider : null,
      gauge: widget === Widget.GAUGE ? params.gauge : null,
      reads: widget === Widget.READOUT && params.reads.length ? params.reads : null,
      rgb,
      ac: widget === Widget.AC ? params.ac : null,
      inputs: params.inputs.length ? params.inputs : null,
    });

    (device?.id
      ? update(ref(db, `devices/${device.id}`), payload)
      : push(ref(db, "devices"), stripNulls(payload))
    )
      .then(close, (err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const tabClass = ({ selected }: { selected: boolean }) =>
    clsx(
      "px-4 py-2 text-sm font-semibold border-b-2 focus:outline-none",
      selected
        ? "border-indigo-500 text-indigo-400"
        : "border-transparent opacity-50 hover:opacity-80"
    );

  const count = paramCount(widget, params);
  const preview = widget === Widget.COLOR && stateKey ? rgbKeys({ stateKey } as INode) : null;

  return (
    <Dialog open={!!device} onClose={close} className="relative z-50">
      <DialogBackdrop transition className={DIALOG_BACKDROP} />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <DialogPanel transition className={DIALOG_PANEL}>
            <div className="py-4 flex justify-between text-xl items-center">
              <h3 className="font-semibold opacity-70">
                {device?.id ? "Edit" : "New"} device
              </h3>
              <button className="text-red-400" onClick={close}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <TabGroup>
              <TabList className="flex border-b border-gray-500/50">
                <Tab className={tabClass}>General</Tab>
                <Tab className={tabClass}>
                  Parameters{count > 0 && ` · ${count}`}
                </Tab>
              </TabList>

              {/* Fixed height, so switching widget never makes the dialog jump. */}
              <TabPanels className="p-5 border-b border-gray-500/50 h-[60vh] overflow-y-auto">
                <TabPanel className="flex-col space-y-5">
                  <TextField label="Title" value={title} onChange={setTitle} />
                  <SelectField
                    label="Room"
                    value={roomId}
                    onChange={setRoomId}
                    options={roomOptions}
                  />
                  <SelectField
                    label="Address"
                    value={addr}
                    onChange={setAddr}
                    options={sourceOptions}
                    hint={
                      sensorOnly
                        ? "Telemetry -- read-only widgets."
                        : `Writes to /${addr || "<addr>"}/...`
                    }
                  />
                  <TextField
                    label="State key"
                    value={stateKey}
                    onChange={setStateKey}
                    list={addr ? `fb-keys-${addr}` : undefined}
                    hint={
                      !addr
                        ? "Pick an address first."
                        : liveKeys.length
                        ? `${liveKeys.length} live keys under /${addr}`
                        : `No live data under /${addr} -- check the board.`
                    }
                  />
                  {/* The id varies with addr on purpose: Chrome caches datalist
                      contents per element+id and a same-id swap keeps stale
                      options. */}
                  <datalist id={`fb-keys-${addr}`}>
                    {liveKeys.map((k) => (
                      <option key={k} value={k} />
                    ))}
                  </datalist>
                  <SelectField
                    label="Widget"
                    value={widget}
                    onChange={(v) => setWidget(v as Widget)}
                    options={widgetOptions}
                    hint={
                      sensorOnly
                        ? "Writing widgets are disabled on a telemetry source."
                        : "Decides how the card is drawn."
                    }
                  />
                  <SelectField
                    label="Type"
                    value={type}
                    onChange={(v) => setType(v as NodeType)}
                    options={Object.values(NodeType).map((t) => ({ value: t, label: t }))}
                    hint="Label only - does not affect rendering."
                  />
                  <NumberField label="Order" value={order} onChange={setOrder} />
                  <SwitchField
                    label="Disabled"
                    checked={disabled}
                    onChange={setDisabled}
                    hint="Hidden from the room page."
                  />
                </TabPanel>

                <TabPanel>
                  {!widget ? (
                    <div className="text-sm opacity-50">
                      Pick a widget on the General tab first.
                    </div>
                  ) : (
                    <>
                      {preview && (
                        <div className="text-xs opacity-50 mb-4">
                          Defaults to {addr}/{preview.r}, {preview.g}, {preview.b}
                        </div>
                      )}
                      {sensorOnly && (
                        <div className="text-xs text-red-400 mb-4">
                          /{addr} is telemetry. Extra inputs write, so any row
                          left below will be refused on save.
                        </div>
                      )}
                      <WidgetParams
                        widget={widget}
                        stateKey={stateKey}
                        state={params}
                        set={setParam}
                        keys={liveKeys}
                        addr={addr}
                      />
                    </>
                  )}
                </TabPanel>
              </TabPanels>
            </TabGroup>

            {error && <div className="px-5 pt-3 text-sm text-red-400">{error}</div>}

            <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                disabled={loading}
                type="button"
                onClick={save}
                className="inline-flex w-full justify-center rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 sm:ml-3 sm:w-32 disabled:opacity-40"
              >
                {loading ? "..." : "Save"}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={close}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-slate-500/20 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-slate-500/40 hover:bg-slate-500/30 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
